<script setup lang="ts">
  import { computed } from 'vue';

  const props = withDefaults(
    defineProps<{
      value?: number;
      min?: number;
      max?: number;
      disabled?: boolean;
      size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
      readonly?: boolean;
      showDeleteButton?: boolean;
    }>(),
    {
      min: 0,
      max: Infinity,
      size: 'md',
      readonly: true,
      showDeleteButton: true,
    }
  );

  const emit = defineEmits(['input']);

  const value = computed({
    get: () => props.value,
    set: (val) => emit('input', Number(val) || 0),
  });

  function increment() {
    value.value = Math.min(value.value + 1, props.max || Infinity);
  }

  function decrement() {
    value.value = Math.max(value.value - 1, props.min);
  }
</script>

<template>
  <div
    class="number-input"
    :class="{
      [`number-input--${props.size}`]: true,
      'number-input--readonly': readonly,
    }"
  >
    <v-btn
      class="action-button"
      icon
      text
      small
      :disabled="disabled || value <= min"
      @click="decrement"
      color="grey"
    >
      <v-icon size="18">{{
        value > 1 || min > 0 || !showDeleteButton ? 'mdi-minus' : 'mdi-delete'
      }}</v-icon>
    </v-btn>

    <v-text-field
      v-model="value"
      class="input"
      dense
      hide-details
      single-line
      type="number"
      :disabled="disabled"
      style="text-align: center"
      :readonly="readonly"
    ></v-text-field>

    <v-btn
      class="action-button"
      icon
      text
      small
      @click="increment"
      :disabled="disabled || value >= (max || Infinity)"
      color="grey"
    >
      <v-icon>mdi-plus</v-icon>
    </v-btn>
  </div>
</template>

<style scoped lang="scss">
  .number-input {
    @apply tw-flex tw-items-center tw-space-x-0 tw-border tw-px-0 tw-rounded-md;
    @apply tw-bg-white;

    .input {
      @apply tw-w-14 tw-text-center;

      ::v-deep .v-input__control {
        min-height: unset;
      }

      ::v-deep .v-input__slot {
        box-shadow: none !important;
        margin-bottom: 0;

        &::before {
          @apply tw-bg-none;
          border: none;
        }

        &::after {
          @apply tw-bg-none;
          border: none;
        }

        input {
          @apply tw-text-center tw-align-middle tw-p-0;
        }
      }
    }

    &.number-input--readonly {
      .input {
        @apply tw-w-8;
      }
    }

    .action-button {
      text-decoration-line: none !important;
    }

    &--xs {
      @apply tw-h-7;

      .action-button {
        @apply tw-text-xl tw-px-2 tw-py-0;
      }
    }

    &--sm {
      @apply tw-h-8;

      .action-button {
        @apply tw-text-2xl tw-px-2 tw-py-0;
      }
    }

    &--md {
      @apply tw-h-9;

      .action-button {
        @apply tw-text-2xl tw-px-3 tw-py-0;
      }
    }

    &--lg {
      @apply tw-h-10;

      .action-button {
        @apply tw-text-3xl tw-px-3 tw-py-0;
      }
    }

    &--xl {
      @apply tw-h-11;

      .action-button {
        @apply tw-text-4xl tw-px-4 tw-py-0;
      }
    }
  }
</style>
